import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import FormRow from '../../common/input/FormRow';
import {
  ENGLISH_NAME_MESSAGE,
  birthdayRules,
  englishNameRules,
} from '../../../constants/validation';
import { putStudentBasicInfo } from '../../../api/utils/student/profile/apis';
import {
  StudentBasicInfoForm,
  toBasicInfoForm,
  toBasicInfoRequest,
} from '../../../api/utils/student/profile/mapper';
import { StudentProfile } from '../../../constants/types/student/profile/type';
import { useSectionSave } from '../../../hooks/student/profile/useSectionSave';
import { Field, ReadOnlyField } from '../../common/form/Field';
import SectionHeader from '../../common/form/SectionHeader';
import AccordionPanel, {
  AccordionControlProps,
} from '../../common/form/AccordionPanel';

/**
 * 학생 페이지 > 내 정보 > 기본 정보 영역
 * @description
 * - 학생이 자신의 기본 정보를 직접 수정합니다.
 * - 폼과 저장은 이 영역이 직접 처리하고, 저장 후 재조회만 부모 컴포넌트에 맡깁니다.
 * - 아이디와 이름은 정책상 수정 불가이므로 값만 보여줍니다. (hint로 수정 불가 표시)
 * - 나머지는 모두 선택 수정이고, 값이 있을 때만 형식을 검사합니다. 값 자체는 모두 필수입니다.
 */
const BasicInfoSection = ({
  profile,
  section,
  onSaved,
}: {
  /** 조회한 내 정보. 폼의 원본 값이자 취소했을 때 되돌릴 값이다. */
  profile: StudentProfile;
  section: AccordionControlProps;
  /** 저장 성공 후 재조회. */
  onSaved: () => void;
}) => {
  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<StudentBasicInfoForm>({
    defaultValues: toBasicInfoForm(profile),
  });

  const isEditing = section.isEditing;
  /** 로그인한 학생의 아이디. 조회 응답에 없어 로그인할 때 저장해 둔 값을 쓴다. */
  const uid = localStorage.getItem('userId') ?? undefined;

  // 재조회로 값이 바뀌면 폼도 그 값으로 맞춘다.
  useEffect(() => {
    reset(toBasicInfoForm(profile));
  }, [profile, reset]);

  const { isSaving, submit } = useSectionSave({
    onClose: section.onClose,
    onSaved,
  });

  /** 입력을 조회 시점으로 되돌리고 수정을 끝낸다. */
  const cancel = () => {
    reset(toBasicInfoForm(profile));
    section.onClose();
  };

  const onSubmit = (form: StudentBasicInfoForm) =>
    submit(async () => {
      const request = toBasicInfoRequest(form, toBasicInfoForm(profile));
      // 바뀐 항목만 보낸다. 보낼 것이 없으면 요청하지 않는다.
      if (Object.keys(request).length === 0) return false;
      await putStudentBasicInfo(request);
      return true;
    });

  /**
   * 저장 전 영어이름의 앞뒤 공백을 제거한다. (형식 검사가 앞뒤 공백을 허용하지 않는다)
   * 화면 값 자체를 바꾸므로 이후의 검증/변경 비교도 공백이 제거된 값으로 수행된다.
   * 생년월일·성별은 직접 입력하는 값이 아니라 공백이 섞이지 않는다.
   */
  const save = () => {
    const englishName = getValues('englishName');
    if (typeof englishName === 'string' && englishName !== englishName.trim()) {
      setValue('englishName', englishName.trim());
    }
    // 검증에 걸리면 handleSubmit이 첫 오류 항목으로 이동시킨다.
    handleSubmit(onSubmit)();
  };

  return (
    <section>
      <SectionHeader
        title="기본 정보"
        isEditing={isEditing}
        disabled={section.disabled}
        isSaving={isSaving}
        onEdit={section.onEdit}
        onCancel={cancel}
        onSave={save}
        isOpen={section.isOpen}
        onToggle={section.onToggle}
      />

      <AccordionPanel isOpen={section.isOpen}>
        <div className="mt-5 grid grid-cols-1 gap-4 pb-2 xl:grid-cols-2">
          <ReadOnlyField label="아이디" value={uid} hint="수정 불가" />

          {/* 아이디 옆 빈 영역 */}
          <div className="hidden xl:block" />

          <ReadOnlyField label="이름" value={profile.name} hint="수정 불가" />

          <Field label="영어이름" error={errors.englishName?.message}>
            <FormRow
              control={control}
              name="englishName"
              type="text"
              uppercase
              placeholder={ENGLISH_NAME_MESSAGE}
              disabled={!isEditing}
              autoComplete="off"
              rules={englishNameRules({ required: true })}
              error={!!errors.englishName}
            />
          </Field>

          <Field label="생년월일" error={errors.birthday?.message}>
            <FormRow
              type="birthday"
              name="birthday"
              control={control}
              rules={birthdayRules({ required: true })}
              disabled={!isEditing}
              error={!!errors.birthday}
            />
          </Field>

          <Field label="성별" error={errors.gender?.message}>
            <FormRow
              type="gender"
              name="gender"
              control={control}
              disabled={!isEditing}
            />
          </Field>
        </div>
      </AccordionPanel>
    </section>
  );
};

export default BasicInfoSection;
